import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useInventoryFlow } from "contexts/InventoryFlowContext";
import type { FlowMode, FlowStep } from "contexts/inventoryFlowReducer";

type InventoryFlowRedirectProps = {
  to: string;
  mode: FlowMode;
  step: FlowStep;
  fallback?: string;
};

const normalizeId = (value?: number | string | null) =>
  value === null || value === undefined ? undefined : String(value);

export default function InventoryFlowRedirect({
  to,
  mode,
  step,
  fallback,
}: InventoryFlowRedirectProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { state, mergeState } = useInventoryFlow();

  useEffect(() => {
    const legacyState = (location.state as Record<string, unknown>) || null;

    if (legacyState) {
      mergeState({
        step,
        mode,
        code:
          (legacyState.pickupCode as number | undefined) ??
          (legacyState.code as number | undefined),
        codeStatus: legacyState.codeStatus as "active" | "used" | undefined,
        codeType: legacyState.codeType as any,
        orderId: legacyState.orderId as string | undefined,
        inventoryId: legacyState.inventoryId as string | undefined,
        item: legacyState.item as any,
        lockerDoorId: normalizeId(legacyState.lockerDoorId as string | number | undefined),
        doorId: legacyState.doorId as string | undefined,
        returnMethod: legacyState.returnMethod as string | undefined,
        doorOpenFailed: legacyState.doorOpenFailed as boolean | undefined,
        contact: legacyState.contact as string | undefined,
        memberId: legacyState.memberId as string | undefined,
        identifierType: legacyState.identifierType as any,
        identifier: legacyState.identifier as string | undefined,
      });

      navigate(to, { replace: true });
      return;
    }

    if (state.step !== "idle" && fallback) {
      navigate(fallback, { replace: true });
      return;
    }

    navigate(to, { replace: true });
  }, [fallback, location.state, mergeState, mode, navigate, state.step, step, to]);

  return null;
}
